/**
 * Curated international subscription / merchant brands for admin import.
 * Slugs must exist in the pinned Simple Icons version (see catalog/SOURCES.md).
 * Aliases are generic match terms only — never derived from user expenses.
 *
 * Brands absent from Simple Icons (trademark removals) can still be added via
 * manual PNG upload in the CMS — they are intentionally omitted here.
 */

export const SIMPLE_ICONS_CDN_VERSION = "16.28.0";

export function simpleIconsSvgUrl(slug: string): string {
  const safe = slug.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  return `https://cdn.jsdelivr.net/npm/simple-icons@${SIMPLE_ICONS_CDN_VERSION}/icons/${safe}.svg`;
}

export type CuratedMerchantIcon = {
  /** Simple Icons slug */
  sourceSlug: string;
  displayName: string;
  /** Primary normalized match key */
  normalizedName: string;
  /** Extra aliases for on-device matching */
  matchTerms: string[];
  /** Brand hex without # (from Simple Icons where known) */
  brandHex: string;
  categoryHint: string;
  sortOrder: number;
};

/** Streaming, music, software, cloud, productivity — verified against pinned CDN. */
export const CURATED_MERCHANT_ICONS: CuratedMerchantIcon[] = [
  {
    sourceSlug: "netflix",
    displayName: "Netflix",
    normalizedName: "netflix",
    matchTerms: ["netflix.com", "nflx"],
    brandHex: "E50914",
    categoryHint: "Entertainment",
    sortOrder: 10,
  },
  {
    sourceSlug: "spotify",
    displayName: "Spotify",
    normalizedName: "spotify",
    matchTerms: ["spotify.com", "spotify ab"],
    brandHex: "1DB954",
    categoryHint: "Entertainment",
    sortOrder: 20,
  },
  {
    sourceSlug: "youtubemusic",
    displayName: "YouTube Music",
    normalizedName: "youtube music",
    matchTerms: ["youtubemusic", "yt music", "google youtube"],
    brandHex: "FF0000",
    categoryHint: "Entertainment",
    sortOrder: 30,
  },
  {
    sourceSlug: "youtube",
    displayName: "YouTube Premium",
    normalizedName: "youtube premium",
    matchTerms: ["youtube", "yt premium"],
    brandHex: "FF0000",
    categoryHint: "Entertainment",
    sortOrder: 35,
  },
  {
    sourceSlug: "appletv",
    displayName: "Apple TV+",
    normalizedName: "apple tv",
    matchTerms: ["apple tv+", "appletv", "tv.apple"],
    brandHex: "000000",
    categoryHint: "Entertainment",
    sortOrder: 40,
  },
  {
    sourceSlug: "applemusic",
    displayName: "Apple Music",
    normalizedName: "apple music",
    matchTerms: ["applemusic", "itunes music"],
    brandHex: "FA243C",
    categoryHint: "Entertainment",
    sortOrder: 45,
  },
  {
    sourceSlug: "hbomax",
    displayName: "Max",
    normalizedName: "max",
    matchTerms: ["hbo max", "hbomax", "max.com"],
    brandHex: "000000",
    categoryHint: "Entertainment",
    sortOrder: 50,
  },
  {
    sourceSlug: "twitch",
    displayName: "Twitch",
    normalizedName: "twitch",
    matchTerms: ["twitch.tv", "twitch interactive"],
    brandHex: "9146FF",
    categoryHint: "Entertainment",
    sortOrder: 60,
  },
  {
    sourceSlug: "playstation",
    displayName: "PlayStation Plus",
    normalizedName: "playstation",
    matchTerms: ["ps plus", "playstation network", "sony playstation"],
    brandHex: "003791",
    categoryHint: "Entertainment",
    sortOrder: 70,
  },
  {
    sourceSlug: "patreon",
    displayName: "Patreon",
    normalizedName: "patreon",
    matchTerms: ["patreon.com"],
    brandHex: "000000",
    categoryHint: "Entertainment",
    sortOrder: 80,
  },
  {
    sourceSlug: "duolingo",
    displayName: "Duolingo",
    normalizedName: "duolingo",
    matchTerms: ["duolingo.com"],
    brandHex: "58CC02",
    categoryHint: "Entertainment",
    sortOrder: 90,
  },
  {
    sourceSlug: "icloud",
    displayName: "iCloud+",
    normalizedName: "icloud",
    matchTerms: ["icloud+", "apple icloud", "icloud storage"],
    brandHex: "3693F3",
    categoryHint: "Bills",
    sortOrder: 100,
  },
  {
    sourceSlug: "googledrive",
    displayName: "Google Drive / One",
    normalizedName: "google drive",
    matchTerms: ["google one", "googleone", "google storage", "googledrive"],
    brandHex: "4285F4",
    categoryHint: "Bills",
    sortOrder: 110,
  },
  {
    sourceSlug: "dropbox",
    displayName: "Dropbox",
    normalizedName: "dropbox",
    matchTerms: ["dropbox.com"],
    brandHex: "0061FF",
    categoryHint: "Bills",
    sortOrder: 120,
  },
  {
    sourceSlug: "notion",
    displayName: "Notion",
    normalizedName: "notion",
    matchTerms: ["notion.so", "notion labs"],
    brandHex: "000000",
    categoryHint: "Bills",
    sortOrder: 130,
  },
  {
    sourceSlug: "zoom",
    displayName: "Zoom",
    normalizedName: "zoom",
    matchTerms: ["zoom.us", "zoom video"],
    brandHex: "0B5CFF",
    categoryHint: "Bills",
    sortOrder: 140,
  },
  {
    sourceSlug: "github",
    displayName: "GitHub",
    normalizedName: "github",
    matchTerms: ["github.com", "github sponsors"],
    brandHex: "181717",
    categoryHint: "Bills",
    sortOrder: 150,
  },
  {
    sourceSlug: "anthropic",
    displayName: "Anthropic / Claude",
    normalizedName: "anthropic",
    matchTerms: ["claude.ai", "claude"],
    brandHex: "191919",
    categoryHint: "Bills",
    sortOrder: 160,
  },
  {
    sourceSlug: "figma",
    displayName: "Figma",
    normalizedName: "figma",
    matchTerms: ["figma.com"],
    brandHex: "F24E1E",
    categoryHint: "Bills",
    sortOrder: 170,
  },
  {
    sourceSlug: "grammarly",
    displayName: "Grammarly",
    normalizedName: "grammarly",
    matchTerms: ["grammarly.com"],
    brandHex: "15C39A",
    categoryHint: "Bills",
    sortOrder: 180,
  },
  {
    sourceSlug: "nordvpn",
    displayName: "NordVPN",
    normalizedName: "nordvpn",
    matchTerms: ["nord vpn", "nordsecurity"],
    brandHex: "4687FF",
    categoryHint: "Bills",
    sortOrder: 190,
  },
  {
    sourceSlug: "1password",
    displayName: "1Password",
    normalizedName: "1password",
    matchTerms: ["1password.com", "agilebits"],
    brandHex: "3B66BC",
    categoryHint: "Bills",
    sortOrder: 200,
  },
];
